/**
 * The billing grid - a spreadsheet, not a card list (brief §37).
 *
 * One scrollable table holding every room in the cycle: 200 for the FRD's
 * reference PG, up to 2000. No paging and no per-room navigation, because the
 * operator's rhythm is "type, tab, type" down a column, and at 200 rooms every
 * extra interaction is paid two hundred times.
 *
 * Three pieces of chrome carry that:
 *   · a STICKY HEAD, so the operator always knows which column they are in;
 *   · FROZEN Room + Status columns (brief §16), so scrolling right to reach
 *     Shoe Care never leaves them looking at an anonymous row of numbers;
 *   · a STICKY FOOTER of visible totals with the per-service breakout (§11),
 *     which is the number the operator reconciles against at the end of a day.
 *
 * `border-collapse: separate` is deliberate: sticky cells lose their borders
 * under `collapse` in Chrome, so each cell draws its own bottom border instead.
 */
import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import type React from 'react'

import { useColors } from '../../../hooks/useColors'
import { formatMoney } from '../format'
import { BILLING_COLUMNS, FROZEN_OFFSETS, GRID_MIN_WIDTH } from './billingColumns'
import BillingRow from './BillingRow'
import type { BillingField } from './BillingRow'
import type { BillingRoomRow, RoomEntry, VisibleTotals } from './billingRowModel'

export interface BillingGridProps {
  rows: BillingRoomRow[]
  totals: VisibleTotals
  /** Unsaved edits, keyed by room. A room's entry changes identity only when
   *  that room is edited, which is what keeps the memo on BillingRow effective. */
  drafts: ReadonlyMap<string, RoomEntry>
  /** Validation is revealed only after a save attempt. */
  showErrors?: boolean
  onEdit: (roomId: string, field: BillingField, value: string) => void
  selected: ReadonlySet<string>
  /** True when every visible row is selected - drives the header checkbox. */
  allSelected: boolean
  /** True when SOME but not all are - drives its indeterminate dash. */
  someSelected: boolean
  onToggleAll: () => void
  onToggleSelect: (roomId: string) => void
  onOpenRoom: (roomId: string) => void
  onOpenItems: (roomId: string, serviceSlug: string) => void
  onOpenMenu: (roomId: string, anchor: DOMRect) => void
  /** Escape in a cell: put back the value it had when the grid last loaded. */
  onRevertCell?: (roomId: string | undefined, column: string) => void
  /** Rendered in place of the rows - empty state, or a loading skeleton. */
  placeholder?: ReactNode
}

export default function BillingGrid({
  rows,
  totals,
  drafts,
  showErrors,
  onEdit,
  selected,
  allSelected,
  someSelected,
  onToggleAll,
  onToggleSelect,
  onOpenRoom,
  onOpenItems,
  onOpenMenu,
  onRevertCell,
  placeholder,
}: BillingGridProps) {
  const colors = useColors()

  // `indeterminate` is a DOM property, not an attribute - React cannot set it
  // declaratively, so it has to be written to the node. Same approach as the
  // Orders page's select-all.
  const selectAllRef = useRef<HTMLInputElement | null>(null)
  const bodyRef = useRef<HTMLTableSectionElement | null>(null)

  /**
   * Spreadsheet movement (brief §30).
   *
   * Enter and the vertical arrows step down a COLUMN, because that is how the
   * work actually goes: an operator weighs room after room, not field after
   * field. Tab is left alone - the browser already walks across a row, and
   * re-implementing it would only break something. Left/Right are left alone
   * too: inside a text box they move the caret, and stealing them would make
   * correcting a typo impossible.
   *
   * Escape restores the cell to the value it had when the grid last loaded,
   * which is the operator's undo for "I typed in the wrong row".
   */
  const onGridKeyDown = (event: React.KeyboardEvent<HTMLTableSectionElement>) => {
    const target = event.target as HTMLElement
    const cell = target.getAttribute?.('data-cell')
    if (!cell) return

    const [column, indexText] = cell.split(':')
    const index = Number(indexText)

    const moveTo = (nextIndex: number) => {
      const next = bodyRef.current?.querySelector<HTMLInputElement>(
        `[data-cell="${column}:${nextIndex}"]`,
      )
      if (!next) return
      event.preventDefault()
      next.focus()
      next.select()
    }

    if (event.key === 'Enter' || event.key === 'ArrowDown') moveTo(index + 1)
    else if (event.key === 'ArrowUp') moveTo(index - 1)
    else if (event.key === 'Escape') {
      event.preventDefault()
      onRevertCell?.(rows[index]?.roomId, column)
      ;(target as HTMLInputElement).blur()
    }
  }
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected
  }, [someSelected])

  const headBase = {
    background: colors.bgSecondary,
    borderBottom: `1px solid ${colors.border}`,
    padding: '8px 10px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.7px',
    textTransform: 'uppercase' as const,
    color: colors.textMuted,
    whiteSpace: 'nowrap' as const,
  }

  const footBase = {
    background: colors.bgSecondary,
    borderTop: `1px solid ${colors.border}`,
    padding: '10px',
    fontSize: '12px',
    fontWeight: 700,
    color: colors.textPrimary,
    whiteSpace: 'nowrap' as const,
  }

  return (
    <div
      data-testid="billing-grid"
      style={{
        // The grid owns the page's remaining height so the head and footer pin
        // against its edges rather than the viewport's.
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        background: colors.bgCard,
      }}
    >
      <table
        style={{
          width: '100%',
          minWidth: `${GRID_MIN_WIDTH}px`,
          borderCollapse: 'separate',
          borderSpacing: 0,
          textAlign: 'left',
        }}
      >
        <thead>
          <tr>
            {BILLING_COLUMNS.map((column) => {
              const isFrozen = Boolean(column.frozen)
              return (
                <th
                  key={column.key}
                  scope="col"
                  style={{
                    ...headBase,
                    position: 'sticky',
                    top: 0,
                    // Frozen header cells pin in BOTH directions, so they need
                    // to outrank the merely-sticky ones on either axis.
                    left: isFrozen ? `${FROZEN_OFFSETS[column.key]}px` : undefined,
                    zIndex: isFrozen ? 3 : 2,
                    width: `${column.width}px`,
                    minWidth: `${column.width}px`,
                    textAlign: column.align ?? 'left',
                  }}
                >
                  {column.key === 'select' ? (
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      aria-label="Select all visible rooms"
                      checked={allSelected}
                      onChange={onToggleAll}
                      style={{
                        width: '14px',
                        height: '14px',
                        accentColor: colors.textPrimary,
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    column.label
                  )}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody ref={bodyRef} onKeyDown={onGridKeyDown}>
          {placeholder ? (
            <tr>
              <td colSpan={BILLING_COLUMNS.length} style={{ padding: 0 }}>
                {placeholder}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <BillingRow
                key={row.roomId}
                row={row}
                rowIndex={rowIndex}
                draft={drafts.get(row.roomId)}
                showErrors={showErrors}
                onEdit={onEdit}
                selected={selected.has(row.roomId)}
                onToggleSelect={onToggleSelect}
                onOpenRoom={onOpenRoom}
                onOpenItems={onOpenItems}
                onOpenMenu={onOpenMenu}
              />
            ))
          )}
        </tbody>

        {/* Visible totals - the filtered view's numbers, not the cycle's. */}
        <tfoot>
          <tr>
            <td
              colSpan={3}
              style={{
                ...footBase,
                position: 'sticky',
                bottom: 0,
                left: 0,
                zIndex: 3,
                fontSize: '10px',
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                color: colors.textMuted,
              }}
            >
              Visible totals
              <span style={{ marginLeft: '8px', color: colors.textSecondary, fontWeight: 600 }}>
                {totals.rooms} room{totals.rooms === 1 ? '' : 's'}
              </span>
            </td>
            <td
              colSpan={3}
              data-testid="totals-weight"
              style={{ ...footBase, position: 'sticky', bottom: 0, zIndex: 2 }}
            >
              <Subtotal label="Wash & Iron" value={formatMoney(totals.weight)} />
            </td>
            <td
              data-testid="totals-dc"
              style={{ ...footBase, position: 'sticky', bottom: 0, zIndex: 2 }}
            >
              <Subtotal label="Dry Cleaning" value={formatMoney(totals.dc)} />
            </td>
            <td
              data-testid="totals-shoe"
              style={{ ...footBase, position: 'sticky', bottom: 0, zIndex: 2 }}
            >
              <Subtotal label="Shoe Care" value={formatMoney(totals.shoe)} />
            </td>
            <td
              data-testid="totals-grand"
              style={{
                ...footBase,
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                textAlign: 'right',
                fontSize: '13px',
              }}
            >
              <Subtotal label="Total billing" value={formatMoney(totals.total)} align="right" />
            </td>
            <td style={{ ...footBase, position: 'sticky', bottom: 0, zIndex: 2 }} />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/**
 * A labelled figure in the totals footer.
 *
 * Brief §11 names each subtotal rather than leaving bare numbers under the
 * columns: once the grid is scrolled, a lone "₹22,450" does not say which
 * service it belongs to.
 */
function Subtotal({ label, value, align }: { label: string; value: string; align?: 'right' }) {
  const colors = useColors()

  return (
    <span style={{ display: 'block', textAlign: align }}>
      <span
        className="font-body"
        style={{
          display: 'block',
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        {label}
      </span>
      {value}
    </span>
  )
}
